<script setup lang="ts">
import { computed } from "vue";
import Multiselect from "vue-multiselect";

export type TFloorplanOption = {
    value: string; // nb_floorplan
    label: string; // filename w/ ext
    chairs: number; // nb_pivot_points - 1
};

const props = defineProps<{
    modelValue: string; // selected floorplan
    floorplanOptions: TFloorplanOption[];
}>();
const emit = defineEmits<(e: "update:modelValue", value: string) => void>();

const groups = computed(() => {
    const map: Map<number, TFloorplanOption[]> = new Map();
    for (const f of props.floorplanOptions) {
        const chairCount = f.chairs;
        if (!map.has(chairCount)) {
            map.set(chairCount, []);
        }
        map.get(chairCount)!.push(f);
    }

    const groups = [...map.entries()]
        .sort((a, b) => Number(a[0]) - Number(b[0]))
        .map(([chairs, options]) => {
            return {
                groupLabel: chairs === 0 ? "Single item" : chairs === 1 ? "1 chair" : `${ chairs } chairs`,
                options: options.sort((a, b) => a.label.localeCompare(b.label)),
            };
        });
    return groups;
});

const multiselectSettings = {
    groupLabel: "groupLabel",
    groupValues: "options",
    trackBy: "value",
    label: "label",
};

function onChange(selected: TFloorplanOption | null) {
    emit("update:modelValue", selected ? selected.value : "");
}

function setSingleItem() {
    const single = props.floorplanOptions.find((f) => f.chairs === 0);
    if (single) {
        emit("update:modelValue", single.value);
    }
}
</script>

<template>
    <div class="amisco-fp-row">
        <button
            type="button"
            class="amisco-button"
            @click="() => setSingleItem()"
        >
            Single Object
        </button>

        <Multiselect
            :options="groups"
            :group-label="multiselectSettings.groupLabel"
            :group-values="multiselectSettings.groupValues"
            :track-by="multiselectSettings.trackBy"
            :label="multiselectSettings.label"
            :show-no-results="false"
            searchable
            close-on-select
            :hide-selected="false"
            internal-search
            :allow-empty="false"
            :model-value="floorplanOptions.find((f) => f.value === modelValue) || null"
            placeholder="Select a floorplan"
            class="amisco-compact-ms"
            @update:model-value="(value: TFloorplanOption) => onChange(value)"
        >
            <template #option="{ option }">
                <div class="amisco-fp-option-row">
                    <span class="amisco-fp-option-label">{{ option.label }}</span>
                    <small
                        v-if="option.chairs !== undefined"
                        style="margin-left:6px;opacity:0.6"
                    >
                        ({{ option.chairs }} chair{{ option.chairs === 1 ? '' : 's' }})
                    </small>
                </div>
            </template>
        </Multiselect>
    </div>
</template>

<style scoped>
.amisco-fp-row {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px;
  align-items: center;
  min-width: 450px;
}
/* Button */
.amisco-btn {
  font-size: 12px;
  line-height: 1;
  padding: 6px 10px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #f8fafc;
  cursor: pointer;
}
.amisco-btn:hover { background: #eef2ff; }

/* Overall compact sizing */
.amisco-compact-ms :deep(.multiselect) {
  font-size: 13px;
  line-height: 1.2;
}
.amisco-compact-ms :deep(.multiselect__single) {
  padding: 1px 8px;
}
.amisco-compact-ms :deep(.multiselect__tags) {
  min-height: 28px;
}

/* Dropdown panel */
.amisco-compact-ms :deep(.multiselect__content-wrapper) {
  max-height: 220px; /* tighter list */
}

/* Group header (divider with label) */
.amisco-compact-ms :deep(.multiselect__option--group) {
  font-weight: 600;
  font-size: 11px;
  letter-spacing: .02em;
  text-transform: uppercase;
  color: #555;
  background: #f6f7f9;
  border-top: 1px solid #e9e9ee;
  padding: 4px 8px;
  position: sticky;   /* nice UX: group label sticks while scrolling */
  top: 0;
  z-index: 1;
}

/* Option rows */
.amisco-fp-option-row {
  padding: 4px 8px;
}
.amisco-compact-ms :deep(.multiselect__option--highlight) .amisco-fp-option-row {
  background: #41b883; /* subtle highlight */
}
.amisco-compact-ms :deep(.multiselect__option) {
  min-height: unset; /* allow compact rows */
}

/* Selected chip/label */
.amisco-fp-selected {
  padding: 2px 0;
}
</style>
